// Cloud voice-note transcription (#393) — the wire contract between the
// extension's Transcribe buttons and the backend `/actions` commands below.
//
// The transcript itself is the shared speech contract
// (`speech-contracts.interface.ts`, `source: 'cloud'`), so every consumer that
// already renders a local Whisper result renders a cloud one unchanged.
//
// Billing: credits per started block of audio (`CloudTranscriptionPricing`,
// default 1 credit per started 2 minutes). Charged once per message (a cache
// hit is free), never on silence or on an error.
//
// Additive evolution only: this crosses the extension↔backend wire.

import { z } from 'zod';
import { TranscriptionResultSchema } from './speech-contracts.interface';

/** `/actions` command names. */
export const CLOUD_TRANSCRIPTION_COMMANDS = {
  /** Is cloud transcription available to this user, and how many credits are left. Never charges. */
  status: 'voice-transcription.status',
  /** Transcribe one voice note. Charges on a successful, non-silent transcript (see the pricing). */
  transcribe: 'voice-transcription.transcribe',
} as const;

/**
 * What one transcription costs: `creditsPerBlock` for every started `blockSeconds` of audio.
 * `blockSeconds: 0` is a flat `creditsPerBlock` per transcription, whatever the length.
 */
export const CloudTranscriptionPricingSchema = z.object({
  creditsPerBlock: z.number().int().min(1),
  blockSeconds: z.number().int().nonnegative(),
});
export type CloudTranscriptionPricing = z.infer<typeof CloudTranscriptionPricingSchema>;

/** 1 credit per started 2 minutes: a 5-minute note costs 3. */
export const DEFAULT_CLOUD_TRANSCRIPTION_PRICING: CloudTranscriptionPricing = { creditsPerBlock: 1, blockSeconds: 120 };

/**
 * Credits for a note of `durationSec`. Always at least one block, so a note whose length
 * is unknown (0 or not a number) is never free.
 */
export function cloudTranscriptionCreditCost(durationSec: number, pricing: CloudTranscriptionPricing): number {
  if (pricing.blockSeconds === 0) return pricing.creditsPerBlock;
  const sec = Number.isFinite(durationSec) && durationSec > 0 ? durationSec : 0;
  return pricing.creditsPerBlock * Math.max(1, Math.ceil(sec / pricing.blockSeconds));
}

/**
 * Credits for the shortest note. Kept for clients that read `creditCost`; new code
 * prices a note with `cloudTranscriptionCreditCost`.
 */
export const CLOUD_TRANSCRIPTION_CREDIT_COST = DEFAULT_CLOUD_TRANSCRIPTION_PRICING.creditsPerBlock;
/** Longest note the cloud path accepts. */
export const CLOUD_TRANSCRIPTION_MAX_SECONDS = 5 * 60;
/** Largest raw audio (before base64) the cloud path accepts. Bounded by the backend's 10 MB JSON body limit. */
export const CLOUD_TRANSCRIPTION_MAX_BYTES = 7 * 1024 * 1024;

export const CloudTranscriptionStatusResponseSchema = z.object({
  /** False when the feature flag is off for this user: the client stays on local Whisper. */
  enabled: z.boolean(),
  /** Credits left in the workspace. Null when the balance could not be read. */
  creditsRemaining: z.number().nullable(),
  /** Credits the shortest transcription costs (one block). */
  creditCost: z.number(),
  /** How a note is priced. Absent from backends that predate per-block pricing. */
  pricing: CloudTranscriptionPricingSchema.optional(),
});
export type CloudTranscriptionStatusResponse = z.infer<typeof CloudTranscriptionStatusResponseSchema>;

export const CloudTranscribeRequestSchema = z.object({
  /** Serialized WhatsApp message key. With the audio hash, the cache + charge-once key. */
  messageKey: z.string().min(1).max(512),
  /** Raw audio bytes, base64 (no data: prefix). */
  audioBase64: z.string().min(1),
  /** e.g. 'audio/ogg; codecs=opus'. */
  mimetype: z.string().min(1).max(100),
  /** Client-measured duration, if known. The server re-measures OGG/Opus and uses the larger. */
  durationSec: z.number().nonnegative().optional(),
  /** ISO-639-1 code the user locked, or 'auto'. */
  languageHint: z.string().min(2).max(10).optional(),
});
export type CloudTranscribeRequest = z.infer<typeof CloudTranscribeRequestSchema>;

/** `ok` = text returned; `silence` = no speech in the audio (not charged). */
export const CloudTranscribeStatusSchema = z.enum(['ok', 'silence']);
export type CloudTranscribeStatus = z.infer<typeof CloudTranscribeStatusSchema>;

export const CloudTranscribeResponseSchema = z.object({
  status: CloudTranscribeStatusSchema,
  transcription: TranscriptionResultSchema,
  /** 0 on a cache hit or silence. */
  creditsCharged: z.number(),
  cached: z.boolean(),
  /** Credits left after this call. Null when the balance could not be read. */
  creditsRemaining: z.number().nullable(),
});
export type CloudTranscribeResponse = z.infer<typeof CloudTranscribeResponseSchema>;

/**
 * Why a transcribe call was refused. Sent as `data.reason` on the error, with
 * the HTTP code in brackets. Every one of them is uncharged.
 *  - disabled            (503) flag off for this user
 *  - insufficient_credits(402) fewer credits left than the note costs
 *  - too_long            (413) over CLOUD_TRANSCRIPTION_MAX_SECONDS
 *  - too_large           (413) over CLOUD_TRANSCRIPTION_MAX_BYTES
 *  - unsupported_audio   (415) a container the engine does not take
 *  - rate_limited        (429) too many requests for this workspace
 *  - engine_unavailable  (503) the engine failed; the client may use local Whisper
 */
export const CloudTranscribeErrorReasonSchema = z.enum([
  'disabled',
  'insufficient_credits',
  'too_long',
  'too_large',
  'unsupported_audio',
  'rate_limited',
  'engine_unavailable',
]);
export type CloudTranscribeErrorReason = z.infer<typeof CloudTranscribeErrorReasonSchema>;

export const CloudTranscribeErrorDataSchema = z.object({
  reason: CloudTranscribeErrorReasonSchema,
  creditsRemaining: z.number().nullable().optional(),
});
export type CloudTranscribeErrorData = z.infer<typeof CloudTranscribeErrorDataSchema>;
